import Layout from "../utils/Layout";
import { useEffect, useState } from "react";
import AlertBar from "../utils/AlertBar";
import IngredientsDetailsDrawer from "../ingredients/IngredientsDetailsDrawer";
import { useNavigate } from "react-router-dom";
import { HeadingTitle } from "../utils/HeadingTitle";
import { API_URL } from "../../config/environment";
import TabContext from "@mui/lab/TabContext";
import * as React from "react";
import SubscriptionAlert from "../utils/SubscriptionAlert";
import { isExceededAccessLimit } from "../../helpers/userUtils";
import { useTranslation } from "react-i18next";
import secureLocalStorage from "react-secure-storage";
import { getIngredientsDataGridColumns } from "../../helpers/dataGridUtils";
import { useTitle } from "../utils/useTitle";

import ChartsOverview from "./utils/ChartsOverview";
import TableOverview from "./utils/TableOverview";
import OverviewTabs from "./utils/TabsOverview";
import DecisionTreeOverview from "./utils/DecisionTreeOverview";
import NewDecisionTree from "./utils/NewDecisionTree";

/**
 * Main Dashboard Component
 * this component displays the Overview page where there are tabs that you can select
 * - Charts: This will display the different charts
 * - Table: this will show the tables with filters
 * - Decision Tree: this will ask you questions and display the correct information
 * 
 * @returns {JSX.Element} Main Dashboard Component
 */
export default function MainDashboard () {
  // Add state for decision tree answers
  const [decisionTreeAnswers, setDecisionTreeAnswers] = useState([]);
  const [decisionTreeIngredients, setDecisionTreeIngredients] = useState(null);
  const [decisionTreeCurrentPage, setDecisionTreeCurrentPage] = useState(0);
  const [decisionTreePageSize, setDecisionTreePageSize] = useState(15);
  const [decisionTreeFilters, setDecisionTreeFilters] = useState([]);

  const [showTable, setShowTable] = useState(false);

  useTitle("Dashboard");

  const navigate = useNavigate();
  const { t } = useTranslation();
  const token = sessionStorage.getItem("authToken");
  const storedUser = secureLocalStorage.getItem("userInfo");
  const user = JSON.parse(storedUser);
  const storedIngredients = secureLocalStorage.getItem("ingredients");
  const storedIngredientsMetrics =
    secureLocalStorage.getItem("ingredientsMetrics");

  const [technofunctionalityParams, setTechnofunctionalityParams] = useState(["Agent emulsifiant",]);
  const [sourceCategoryParams, setSourceCategoryParams] = useState([""]);
  const [sectorParams, setSectorParams] = useState([""]);
  const [productPlatformParams, setProductPlatformParams] = useState([""]);

  const [ingredients, setIngredients] = useState(null);
  const [ingredientsMetrics, setIngredientsMetrics] = useState(null);

  const [loading, setLoading] = useState(true);
  const [trigger, setTrigger] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isSubscriptionAlertOpen, setIsSubscriptionAlertOpen] = useState(false);
  const [selectInputInitialValue, setSelectInputInitialValue] = useState(["Agent emulsifiant",]);
  const [tab, setTab] = useState("1");

  const handleChangeTab = (event, newTab) => {
    setTab(newTab);

    setDecisionTreeIngredients(null);
    setShowTable(false);
  };

  const ingredientParams = [
    "Technofunctionality",
    "Source Category",
    "Sector",
    "Product Platform",
  ];

  const columns = getIngredientsDataGridColumns(t);

  const categoryCodePrefixes = {
    substitution_de_matiere_grasse: "FTS-",
    selection_des_antimicrobiens: "ANM-",
    substitution_des_sucres: "SGS-",
    selection_des_antioxydants: "ANX-",
  };

  const [decisionTreeCodePrefix, setDecisionTreeCodePrefix] = useState(null); // For the pagination
  
  const handleDecisionTreeReset = () => {
    setDecisionTreeIngredients(null);
    setShowTable(false);
  };

  function handleIsErrorState() {
    setIsError(false);
  }

  const handleCloseSubscriptionAlert = () => {
    setIsSubscriptionAlertOpen(false);
  };

  const openDrawer = (rowData) => {
    setSelectedRow(rowData);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setSelectedRow(null);
    setDrawerOpen(false);
  };

  function getParams(params, label) {
    if (label === "Technofunctionality") {
      setTechnofunctionalityParams(params);
    } else if (label === "Source Category") {
      setSourceCategoryParams(params);
    } else if (label === "Sector") {
      setSectorParams(params);
    } else if (label === "Product Platform") {
      setProductPlatformParams(params);
    }
  }

  function checkAccessLimit(page, pageSize) {
    if (isExceededAccessLimit(ingredients?.total, page, pageSize, user)) {
      setIsSubscriptionAlertOpen(true);
      return ingredients;
    }

    setCurrentPage(page);
    setPageSize(pageSize);
    handlePaginationChange(page, pageSize);
  }

  async function getIngredients(
    technofunctionality,
    sourceCategory,
    productPlatform,
    sector,
    page = 0,
    offset = 15
  ) {
    try {
      let _technofunctionality;

      // AB: Not to fall back to defaults. Use whatever is in the state.
      if (technofunctionality && Array.isArray(technofunctionality)) {
        _technofunctionality = technofunctionality?.join(";");
      } else {
        _technofunctionality = technofunctionality; // || ""; // Empty string if no value is set.
      }

      // if (technofunctionality) {
      //   if (Array.isArray(technofunctionality)) {
      //     _technofunctionality = technofunctionality?.join(";");
      //   } else {
      //     _technofunctionality = technofunctionality;
      //   }
      // } else {
      //   _technofunctionality = "Agent emulsifiant";
      // }

      const _sourceCategory = sourceCategory ? [sourceCategory]?.join(";") : "";
      const _productPlatform = productPlatform
        ? [productPlatform]?.join(";")
        : "";
      const _sector = sector ? [sector]?.join(";") : "";

      const url = `${API_URL}/ingredients?page=${page}&offset=${offset}&technofunctionality=${_technofunctionality}&sourceCategory=${_sourceCategory}&productPlatform=${_productPlatform}&sector=${_sector}`;

      try {
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();

        if (response.status === 401) {
          navigate("/signin");
        }

        if (data.status === "success") {
          const ingredients = data.ingredients;
          const _ingredients = JSON.stringify(ingredients);
          secureLocalStorage.setItem("ingredients", _ingredients);
          return ingredients;
        } else {
          // console.log("Could not fetch data from server");
          setIsError(true);
          setErrorMessage(data.message);
        }
      } catch (error) {
        // console.log("Error fetching ingredients");
        setIsError(true);
        setErrorMessage("Error fetching ingredients");
      }
    } catch (error) {
      // console.log("Error processing request");
      setIsError(true);
      setErrorMessage("Error processing request");
    }
  }

  async function getIngredientsMetrics() {
    const url = `${API_URL}/ingredients/metrics`;
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (response.status === 401) {
        navigate("/signin");
      }

      if (data.status === "success") {
        let ingredientsMetrics = data.ingredientsMetrics;

        ingredientsMetrics = restructureMetricsData(ingredientsMetrics);
        const _ingredientsMetrics = JSON.stringify(ingredientsMetrics);
        secureLocalStorage.setItem("ingredientsMetrics", _ingredientsMetrics);
        return ingredientsMetrics;
      } else {
        setIsError(true);
        setErrorMessage(data.message);
        return;
      }
    } catch (error) {
      console.log("Error fetching ingredients from server");
    }
  }

  function restructureMetricsData(metrics) {
    metrics = metrics?.map((metric) => {
      const metricsData = {
        categories: ["Sectors", "ProductPlaforms", "Ingredients"],
        series: [
          {
            name: "sectors",
            data: metric.metrics.sectorMetrics,
          },
          {
            name: "productPlatform",
            data: metric.metrics.productPlatformMetrics,
          },
          {
            name: "ingredients",
            data: metric.metrics.ingredientMetrics,
          },
        ],
      };

      return { name: metric.name, metricsData };
    });

    return metrics;
  }

  useEffect(() => {
    if (!token) {
      return navigate("/signin");
    }
  }, [navigate, token]);

  useEffect(() => {
    const fetchMetrics = async () => {
      const _ingredientsMetrics = await getIngredientsMetrics();
      // const data = restructureMetricsData(_ingredientsMetrics);
      setIngredientsMetrics(_ingredientsMetrics);
      setLoading(false);
    };

    if (
      storedIngredients &&
      Object?.keys(storedIngredientsMetrics)?.length > 0
    ) {
      const _storedIngredientsMetrics = JSON.parse(storedIngredientsMetrics);
      // const data = restructureMetricsData(storedIngredientsMetrics);
      setIngredientsMetrics(_storedIngredientsMetrics);
      setLoading(false);
    } else {
      fetchMetrics();
    }
  }, [storedIngredientsMetrics]);

  useEffect(() => {
    const fetchData = async () => {
      const ingredients = await getIngredients(
        technofunctionalityParams,
        sourceCategoryParams,
        productPlatformParams,
        sectorParams
      );
      setIngredients(ingredients);
      setLoading(false);
    };

    if (storedIngredients && Object?.keys(storedIngredients)?.length > 0) {
      const _storedIngredients = JSON.parse(storedIngredients);
      setIngredients(_storedIngredients);
      setLoading(false);
    } else {
      fetchData();
    }
  }, [storedIngredients]);

  useEffect(() => {
    if (decisionTreeAnswers && decisionTreeAnswers.length > 0) {
      // fetchIngredientsWithAnswers(decisionTreeAnswers);
    }
  }, [decisionTreeAnswers]);

  const handleDecisionTreeSubmit = async (results) => {
    if (!results) return;
    try {
      const mappedIngredientsData = results.map((item) => ({
        ...item,
        id: item._id || item.id,
      }));
      setDecisionTreeCurrentPage(0);
      setDecisionTreePageSize(15);

      setDecisionTreeIngredients({
        ingredientsData: mappedIngredientsData,
        total: results?.length,
      });

      // Show the table after successful submission
      setShowTable(true);
    } catch (error) {
      console.error("Error fetching ingredients data:", error);
      // alert("An error occurred while fetching the data. Please try again.");
    }
  };

  async function handlePaginationChange(page, offset) {
    setLoading(true);

    const ingredients = await getIngredients(
      technofunctionalityParams,
      sourceCategoryParams,
      productPlatformParams,
      sectorParams,
      page,
      offset
    );
    setIngredients(ingredients);
    setLoading(false);
  }

  async function handleParamsSubmit() {
    setLoading(true);
    setTrigger(true);

    const ingredients = await getIngredients(
      technofunctionalityParams,
      sourceCategoryParams,
      productPlatformParams,
      sectorParams
    );
    setIngredients(ingredients);
    setTrigger(false);
    setLoading(false);
  }

  async function handleParamsReset() {
    const technofunctionality = ["Agent emulsifiant"];
    const sourceCategory = [""];
    const productPlatform = [""];
    const sector = [""];
    const page = 0;
    const offset = 15;

    setTechnofunctionalityParams(technofunctionality);
    setSourceCategoryParams(sourceCategory);
    setSectorParams(sector);
    setProductPlatformParams(productPlatform);
    const ingredients = await getIngredients(
      technofunctionality,
      sourceCategory,
      productPlatform,
      sector,
      page,
      offset
    );

    setIngredients(ingredients);
    setCurrentPage(0);
    setPageSize(offset);
    setSelectInputInitialValue([]);

    // Clear the table
    setDecisionTreeIngredients(null);
    setShowTable(false);
  }

  return (
    <Layout>
      <HeadingTitle text={t("dashboard.label.header")} />
      <TabContext value={tab}>
        <OverviewTabs t={t} handleChangeTab={handleChangeTab} />
        
        <ChartsOverview ingredientsMetrics={ingredientsMetrics} user={user} />

        <TableOverview 
          t={t} 
          ingredientParams={ingredientParams} 
          getParams={getParams} 
          handleParamsReset={handleParamsReset} 
          technofunctionalityParams={technofunctionalityParams} 
          handleParamsSubmit={handleParamsSubmit} 
          ingredients={ingredients} 
          loading={loading} 
          columns={columns} 
          pageSize={pageSize} 
          currentPage={currentPage} 
          checkAccessLimit={checkAccessLimit} 
          openDrawer={openDrawer} 
        />

        <DecisionTreeOverview
          setDecisionTreeAnswers={setDecisionTreeAnswers}
          handleDecisionTreeReset={handleDecisionTreeReset}
          handleDecisionTreeSubmit={handleDecisionTreeSubmit}
          showTable={showTable}
          decisionTreeIngredients={decisionTreeIngredients}
          loading={loading}
          columns={columns}
          decisionTreeCurrentPage={decisionTreeCurrentPage}
          decisionTreePageSize={decisionTreePageSize}
          setDecisionTreeCurrentPage={setDecisionTreeCurrentPage}
          setDecisionTreePageSize={setDecisionTreePageSize}
          openDrawer={openDrawer}
        />
        
        <NewDecisionTree
          ingredients={ingredients} 
          loading={loading} 
          columns={columns} 
          pageSize={pageSize} 
          currentPage={currentPage} 
          checkAccessLimit={checkAccessLimit} 
          openDrawer={openDrawer} 
        />
        
      </TabContext>

      <IngredientsDetailsDrawer
        isOpen={drawerOpen}
        onClose={closeDrawer}
        selectedRowId={selectedRow?.id}
        view_from={"Table"}
        userId={user?.userId}
      />

      <AlertBar
        open={isError}
        message={errorMessage}
        severity={"error"}
        onClose={handleIsErrorState}
      />

      <SubscriptionAlert
        open={isSubscriptionAlertOpen}
        onClose={handleCloseSubscriptionAlert}
      />
    </Layout>
  );
};

